import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export type ScheduleMode = 'CALENDAR' | 'RUNNING_HOURS' | 'CONDITION';

const MODES: Array<{
  value: ScheduleMode;
  title: string;
  description: string;
}> = [
  {
    value: 'CALENDAR',
    title: 'Calendar',
    description: 'Due by date — every N days, weeks, or months.',
  },
  {
    value: 'RUNNING_HOURS',
    title: 'Counter',
    description: 'Due by meter reading — running hours, starts, or cycles.',
  },
  {
    value: 'CONDITION',
    title: 'Observation',
    description: 'Triggered by inspection findings, not a fixed interval.',
  },
];

export const ScheduleModePicker: React.FC<{
  value?: string | null;
  onChange: (value: ScheduleMode) => void;
  error?: string;
}> = ({ value, onChange, error }) => {
  const { colors } = useTheme();

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {MODES.map((mode) => {
          const selected = value === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              onClick={() => onChange(mode.value)}
              aria-pressed={selected}
              className={cn(
                'text-left rounded-lg border-2 px-4 py-3 transition-all duration-150',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                selected
                  ? cn(colors.primaryBorderAll, colors.surfaceHover)
                  : cn(colors.border, colors.surface, colors.interactiveHover),
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                    selected ? cn(colors.primary, colors.primaryBorderAll) : colors.border,
                  )}
                  aria-hidden
                >
                  {selected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                </span>
                <span className="min-w-0">
                  <span className={cn('block text-sm font-semibold', colors.text)}>{mode.title}</span>
                  <span className={cn('mt-1 block text-xs leading-snug', colors.textSecondary)}>
                    {mode.description}
                  </span>
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
};
